import { Ref, ref } from 'vue'
import { LocationQueryValue } from 'vue-router'

import { StateField, StateFieldConfig } from '../StateField'

export type DateRangeStateFieldConfig =
  StateFieldConfig<DateRangeStateField> & {
    type: 'daterange'
    default?: { start?: Date | null; end?: Date | null }
    overrideValue?: { start?: Ref<Date | null>; end?: Ref<Date | null> }

    minDate?: Date
    maxDate?: Date
    inputPopupPlacement?: 'top' | 'bottom'
    inputPlaceholder?: string
    includeTime?: boolean
  }

export class DateRangeStateField extends StateField {
  readonly type = 'daterange'
  default: { start: Date | null; end: Date | null }
  value: {
    start: Ref<Date | null>
    end: Ref<Date | null>
  }

  minDate?: Date
  maxDate?: Date
  inputPopupPlacement?: 'top' | 'bottom'
  inputPlaceholder?: string
  includeTime?: boolean

  constructor(config: DateRangeStateFieldConfig) {
    super(config)

    this.value = {
      start: config.overrideValue?.start || ref(config.default?.start || null),
      end: config.overrideValue?.end || ref(config.default?.end || null)
    }

    this.default = {
      start: config.default?.start || null,
      end: config.default?.end || null
    }

    this.minDate = config.minDate
    this.maxDate = config.maxDate
    this.inputPopupPlacement = config.inputPopupPlacement
    this.inputPlaceholder = config.inputPlaceholder
    this.includeTime = config.includeTime
  }

  toRouteValue(): string | undefined {
    if (this.toRouteValueOverride) return this.toRouteValueOverride(this)

    const start = this.value.start.value
      ? this.value.start.value.toISOString()
      : undefined
    const end = this.value.end.value
      ? this.value.end.value.toISOString()
      : undefined

    if (
      start === this.default.start?.toISOString() &&
      end === this.default.end?.toISOString()
    ) {
      return undefined
    }

    return `${start},${end}`
  }

  pullRouteQueryValue(value: LocationQueryValue | LocationQueryValue[]): void {
    if (this.pullRouteQueryValueOverride)
      return this.pullRouteQueryValueOverride(this, value)

    if (Array.isArray(value)) {
      this.value.start.value = value[0] ? new Date(value[0].toString()) : null
      this.value.end.value = value[1] ? new Date(value[1].toString()) : null
    } else {
      this.resetValue()
    }
  }

  pullRouteParamValue(value: string | string[]): void {
    if (this.pullRouteParamValueOverride)
      return this.pullRouteParamValueOverride(this, value)

    if (Array.isArray(value)) {
      this.value.start.value = value[0] ? new Date(value[0].toString()) : null
      this.value.end.value = value[1] ? new Date(value[1].toString()) : null
    } else {
      this.resetValue()
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async toRequestVariable(variation: number): Promise<any> {
    if (this.toRequestVariableOverride)
      return await this.toRequestVariableOverride(this, variation)

    if (variation === 0) {
      return this.value.start.value?.toISOString()
    }
    if (variation === 1) {
      return this.value.end.value?.toISOString()
    }
    if (variation === 2) {
      return [this.value.start.value, this.value.end.value]
    }
    if (variation === 3) {
      return { start: this.value.start.value, end: this.value.end.value }
    }
    return this.value.start.value
  }

  resetValue(): void {
    if (this.resetValueOverride) return this.resetValueOverride(this)

    this.value.start.value = this.default.start || null
    this.value.end.value = this.default.end || null
  }
}
